import styles from './dataTypeList.module.css'
import { useEffect, useMemo, useState } from "react"
import { Column, FieldValue, FilterField, Filters, O, Table2, Action, Button, ModalId, useUIActions, BooleanField, InfoSection, useLocalStorageActions, FiltersOptions, FieldType, showToast, ToastKind } from "../../../ui"
import { DataType } from "../../../../tables"
import { Params } from "../../../../state/reducers/types"
import { RoutePath } from "../../../routes"
import { useLocation } from "react-router-dom"
import { dedupe, toList } from '../../../../utils/listUtils'
import { isISOStringDate, toDate } from '../../../../utils/typeUtils'
import { BulkAction } from '../bulk-edit-modal/types'
import { Data, Data2 } from '../../types'
import { ListResponse } from '../../../../modules/common'
import { BulkEditModal } from '../bulk-edit-modal/bulkEditModal'
import { DEFAULT_SORT_DISABLED_HINT, INITIAL_SORT_STATE, MAX_SORT_ITEMS, SORT_BATCH_SIZE, SortState, nextSortState, sortLimitExceededMessage, sortRows, useTableSortLabels, withSortHeaders } from './sortUtils'

type Props = {
    // Si los filtros acotan lo suficiente la lista para ordenarla (ordenar carga todos los registros del filtro)
    canSort?: (filtersData: Data2) => boolean
    sortDisabledHint?: string
    columns: Column[]
    pageSize?: number
    filtersOptions?: FiltersOptions
    bulkActions?: BulkAction[]
    infoRows?: { label: string, value: string }[]
    emptyMessage?: string
    readOnly?: boolean
    toRow: (dataType: DataType) => Data2
    actions?: (rowIdValue: string) => Promise<Action[]>
    loadBatch: (filtersData?: Data2, batchSize?: number, startKey?: Params) => Promise<ListResponse<DataType>>
    loadBatchData?: (items: DataType[]) => Promise<void>
    onLoadBatch?: (filtersData: Data2, items: DataType[]) => void
}

// TODO Este es el definitivo. Migrar DataTypeList y DataTypeList2 a este.
export const DataTypeList3 = (props: Props) => {
    const { canSort, sortDisabledHint = DEFAULT_SORT_DISABLED_HINT, columns, pageSize = 10, filtersOptions, bulkActions = [], infoRows = [], emptyMessage, readOnly, toRow, actions, loadBatch, loadBatchData, onLoadBatch } = props
    const { filtersKey, filtersTextAffix, firstRowLength } = filtersOptions || {}

    const { getFiltersData } = useLocalStorageActions()
    const { defaultFiltersData, initialUntouchedDefaultKeys } = useMemo(() => {
        const storedData: Data2 = filtersKey ? getFiltersData()[filtersKey] || {} : {}
        Object.keys(storedData).forEach(key => {
            const value = storedData[key] as O<string>
            if (isISOStringDate(value)) {
                storedData[key] = toDate(value)
            }
        })
        // Los filtros por defecto solo se aplican a los que el usuario no tiene guardados
        const defaults: Data2 = {}
        filtersOptions?.filterFields?.forEach(filterField => {
            if (filterField.defaultValue !== undefined) {
                defaults[filterField.name] = filterField.defaultValue
            }
        })
        Object.assign(defaults, filtersOptions?.defaultFiltersData)
        const untouchedKeys = Object.keys(defaults).filter(key => storedData[key] === undefined)
        const defaultData: Data2 = { ...storedData }
        untouchedKeys.forEach(key => defaultData[key] = defaults[key])
        return { defaultFiltersData: defaultData, initialUntouchedDefaultKeys: untouchedKeys }
    }, [])

    const [pageItems, setPageItems] = useState<DataType[]>([])
    const [allItems, setAllItems] = useState<DataType[]>([])
    const [allLoaded, setAllLoaded] = useState(false)
    const [buffer, setBuffer] = useState<DataType[]>([])
    const [cursor, setCursor] = useState<O<Params>>()
    const [loadedPagesItems, setLoadedPagesItems] = useState<Record<number, DataType[]>>([])
    const [filtersData, setFiltersData] = useState<Data2>(defaultFiltersData)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [allSelected, setAllSelected] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)
    const [untouchedDefaultKeys, setUntouchedDefaultKeys] = useState<string[]>(initialUntouchedDefaultKeys)

    // Al buscar por texto se ignoran los filtros por defecto que el usuario no tocó (ej. rango de fechas), para encontrar registros viejos
    const queryFiltersData = useMemo(() => {
        const isSearching = !!(filtersData.searchText as O<string>)?.trim()
        if (!isSearching || untouchedDefaultKeys.length === 0) return filtersData
        const data: Data2 = { ...filtersData }
        untouchedDefaultKeys.forEach(key => delete data[key])
        return data
    }, [filtersData, untouchedDefaultKeys])

    const { pathname } = useLocation()

    const { openModal } = useUIActions()

    const sortEnabled = !canSort || canSort(queryFiltersData)

    // Carga el resto del filtro en lotes grandes, con sus datos relacionados (clientes, entregas, etc.) para ordenar y mostrar
    // todas las páginas. Si supera el máximo no guarda nada y devuelve false.
    const loadAllRemainingItems = async (currentCursor: O<Params>, currentAllItems: DataType[]) => {
        let cur = currentCursor
        const newItems: DataType[] = []
        while (cur) {
            const { dataTypes: more, lastKey: next } = await loadBatch(queryFiltersData, SORT_BATCH_SIZE, cur)
            newItems.push(...more)
            cur = next
            if (currentAllItems.length + newItems.length > MAX_SORT_ITEMS) return false
        }
        const all = [...currentAllItems, ...newItems]
        loadBatchData && await loadBatchData(all)
        setAllItems(all)
        setBuffer([])
        setCursor(undefined)
        setAllLoaded(true)
        return true
    }

    const onColumnSort = async (columnName: string) => {
        const newSort = nextSortState(sortState, columnName)
        if (newSort.column && !allLoaded) {
            setLoading(true)
            try {
                const loaded = await loadAllRemainingItems(cursor, allItems)
                if (!loaded) {
                    showToast(sortLimitExceededMessage, ToastKind.YELLOW)
                    return
                }
            } finally {
                setLoading(false)
            }
        } else if (newSort.column && allItems.length > MAX_SORT_ITEMS) {
            showToast(sortLimitExceededMessage, ToastKind.YELLOW)
            return
        } else if (newSort.column && sortState.column === null && loadBatchData) {
            // Todo cargado en la primera tanda: solo la primera página tiene sus datos relacionados
            setLoading(true)
            try {
                await loadBatchData(allItems)
            } finally {
                setLoading(false)
            }
        }
        setSortState(newSort)
        setCurrentPage(1)
    }

    const sortColumn = columns.find(c => c.name === sortState.column)
    const allSortRows = useMemo(() => sortState.direction ? allItems.map(toRow) as Data[] : [], [allItems, sortState.direction, toRow])
    const tableSortLabels = useTableSortLabels(allSortRows, sortColumn)

    const sortedRows = useMemo((): Data[] => {
        if (!sortState.column || !sortState.direction) return pageItems.map(toRow) as Data[]

        if (!sortColumn || sortColumn.sortable === false) return pageItems.map(toRow) as Data[]

        const sorted = sortRows(allSortRows, sortColumn, sortState.direction, tableSortLabels)
        const start = (currentPage - 1) * pageSize
        return sorted.slice(start, start + pageSize)
    }, [allSortRows, pageItems, sortState, sortColumn, toRow, currentPage, pageSize, tableSortLabels])

    const filtersText = useMemo(() =>
        [...Object.values(filtersData), filtersTextAffix]
            .map(value => value instanceof Date ? value.toISOString() : value)
            .filter(Boolean)
            .join(';')
    , [filtersData, filtersTextAffix])

    const setItems = async (items: DataType[]) => {
        setPageItems(items)
        loadBatchData && await loadBatchData(items)
    }

    const loadFirstPage = async () => {
        setLoading(true)
        setCurrentPage(1)
        setSortState(INITIAL_SORT_STATE)
        const { dataTypes, lastKey } = await loadBatch(queryFiltersData, pageSize)
        onLoadBatch && onLoadBatch(queryFiltersData, dataTypes)
        setAllItems(dataTypes)
        setAllLoaded(!lastKey)
        const firstBuffer = dataTypes.slice(pageSize)
        setBuffer(firstBuffer)
        const firstPageItems = dataTypes.slice(0, pageSize)
        await setItems(firstPageItems)
        setLoadedPagesItems({ 1: firstPageItems })
        setCursor(lastKey)
        setLoading(false)
    }

    useEffect(() => {
        const timeoutId = setTimeout(loadFirstPage, 500)
        return () => clearTimeout(timeoutId)
    }, [filtersText])

    const onSearch = (value?: FieldValue) => {
        setFiltersData(prev => ({ ...prev, searchText: value as O<string> }))
    }

    const searchField: O<FilterField> = filtersOptions?.search ? {
        name: 'search',
        type: FieldType.TEXT,
        placeholder: filtersOptions?.search?.placeholder || 'Buscar por texto',
        icon: 'search',
        onChange: onSearch
    } : undefined

    const onChangeFilter = (name: string) => (value?: FieldValue) => {
        setFiltersData(prev => ({ ...prev, [name]: value }))
        setUntouchedDefaultKeys(prev => prev.filter(key => key !== name))
    }

    const filterFields = filtersOptions?.filterFields?.map(filterField => {
        return {
            ...filterField,
            defaultValue: filtersData[filterField.name],
            onChange: onChangeFilter(filterField.name)
        }
    })

    const onToggleCheckAll = (value?: boolean) => {
        const pageIds = pageItems.map(item => item.id)
        setAllSelected(!!value)
        setSelectedIds(prevIds => value ?
            dedupe(prevIds.concat(pageIds)) :
            prevIds.filter(prevId => !pageIds.includes(prevId)))
    }

    const onToggleCheck = (id: string) => (value?: boolean) => {
        setSelectedIds(prevIds => value ? prevIds.concat(id) : prevIds.filter(prevId => prevId !== id))
    }

    let tableColumns: Column[] = [...columns]
    if (bulkActions.length > 0) {
        const checkColumn: Column = {
            name: 'check',
            type: FieldType.BOOLEAN,
            label: '',
            sortable: false,
            renderLabel: () => (
                <BooleanField
                    id="check-all"
                    value={allSelected}
                    onChange={onToggleCheckAll}
                />
            ),
            render: (value?: FieldValue) => {
                const rowIdValue = value! as string
                return (
                    <BooleanField
                        id={`check-${rowIdValue}`}
                        value={selectedIds.includes(rowIdValue)}
                        onChange={onToggleCheck(rowIdValue)}
                    />
                )
            }
        }
        tableColumns = toList(checkColumn).concat(columns)
    }

    const sortableTableColumns = withSortHeaders(tableColumns, sortState, onColumnSort, sortEnabled ? undefined : sortDisabledHint)

    const goToPreviousPage = async () => {
        if (sortState.column) {
            setCurrentPage(prev => prev - 1)
            return
        }
        setLoading(true)
        const previousPage = currentPage - 1
        setCurrentPage(previousPage)
        await setItems(loadedPagesItems[previousPage] || [])
        setLoading(false)
    }

    const onGoToNextPage = async () => {
        if (sortState.column) {
            setCurrentPage(prev => prev + 1)
            return
        }
        setLoading(true)
        const nextPage = currentPage + 1
        setCurrentPage(nextPage)

        let newPageItems: DataType[] = []
        if (loadedPagesItems[nextPage]) {
            newPageItems = loadedPagesItems[nextPage]
            await setItems(newPageItems)
        } else if (buffer.length > pageSize) {
            newPageItems = buffer.slice(0, pageSize)
            await setItems(newPageItems)
            const newBuffer = buffer.slice(pageSize)
            setBuffer(newBuffer)
        } else if (cursor) {
            const { dataTypes, lastKey } = await loadBatch(queryFiltersData, pageSize, cursor)
            const allNewItems = [...buffer, ...dataTypes]
            newPageItems = allNewItems.slice(0, pageSize)
            await setItems(newPageItems)
            setBuffer(allNewItems.slice(pageSize))
            setCursor(lastKey)
            setAllItems(prev => [...prev, ...dataTypes])
            if (!lastKey) setAllLoaded(true)
        } else {
            newPageItems = buffer
            await setItems(newPageItems)
            setBuffer([])
            setAllLoaded(true)
        }

        setLoadedPagesItems(prev => ({
            ...prev,
            [nextPage]: newPageItems
        }))

        setLoading(false)
    }

    const onRefresh = () => {
        setSelectedIds([])
        setAllSelected(false)
        loadFirstPage()
    }
    const onBulkEdit = () => openModal(ModalId.BULK_EDIT)
    const basePath = pathname.replace(/\/$/, '')

    const renderHeaderActions = () => {
        const headerActions = [
            <Button
                key="refresh"
                kind="tertiary"
                icon="arrow-clockwise"
                tooltip="Refrescar"
                disabled={loading}
                round
                rightMargin={!readOnly}
                onClick={onRefresh}
            />
        ]
        const selectedAmount = selectedIds.length
        if (selectedAmount > 0) {
            headerActions.splice(0, 0, (
                <Button
                    key="bulk"
                    kind="primary"
                    label={selectedAmount.toString()}
                    icon="list-check"
                    rightMargin={!readOnly}
                    onClick={onBulkEdit}
                />
            ))
        }
        if (!readOnly) {
            headerActions.push(
                <Button
                    key="create"
                    label="Crear Nuevo"
                    href={`${basePath}/${RoutePath.CREATE}`}
                />
            )
        }
        return headerActions
    }

    const infoSection = useMemo(() => (
        <InfoSection
            content={(
                <div className={styles.infoContainer}>
                    {infoRows.map(infoRow => (
                        <div key={infoRow.label} className={styles.infoField}>
                            <span>{infoRow.label}</span>
                            <span>{infoRow.value}</span>
                        </div>
                    ))}
                </div>
            )}
            loaded={!loading}
        />
    ), [infoRows, loading])

    const sortActive = !!sortState.column && !!sortState.direction

    return (
        <>
            {filtersKey && (
                <Filters
                    filtersKey={filtersKey}
                    searchField={searchField}
                    fields={filterFields as O<FilterField[]>}
                    firstRowLength={firstRowLength}
                />
            )}
            {infoRows.length > 0 && infoSection}
            <Table2
                columns={sortableTableColumns}
                rows={sortedRows}
                rowId="id"
                currentPage={currentPage}
                showNext={sortActive
                    ? currentPage * pageSize < allItems.length
                    : buffer.length > 0 || (loadedPagesItems[currentPage + 1]?.length > 0)
                }
                onGoToPreviousPage={goToPreviousPage}
                onGoToNextPage={onGoToNextPage}
                renderHeaderActions={renderHeaderActions}
                actions={actions}
                loading={loading}
                emptyMessage={emptyMessage}
                readOnly={readOnly}
            />
            {bulkActions.length > 0 && (
                <BulkEditModal
                    ids={selectedIds}
                    bulkActions={bulkActions}
                />
            )}
        </>
    )
}
